import { clone, isNil, omit, pickBy } from 'lodash';
import { RequestParams } from 'shared/interfaces/Api';
import { formatSearchQuery } from 'utils/formatters';
import { calculatePageOffset } from 'utils/helpers/pagination';
export const DEFAULT_PAGE_SIZE = 25;
export const FETCH_ALL_LIMIT = 500;

/**
 * Format query params to request a paginated resource with
 * offset, limit, filters, search and sort parameters
 * @param queryParams - Non formatted params
 * @param options - Extra options like the page limit
 */
export const formatPaginationQueryParams = (qs: ({
  search?: string;
  page?: number;
  sort?: string;
} & Record<string, any>) | undefined, options: {
  limit: number | undefined;
} = {
  limit: DEFAULT_PAGE_SIZE
}) => {
  const queryParams = clone(qs) || {};
  const effectiveLimit = queryParams.limit ?? options.limit;
  queryParams.sort = Array.isArray(queryParams.sort) ? queryParams.sort.join(',') : queryParams.sort;
  const params: RequestParams = {
    // Filter undefined values in the object (ex. {a: 'value', b: undefined} becomes { a: 'value' } )
    ...pickBy(queryParams, q => !isNil(q)),
    ...formatSearchQuery(queryParams.search),
    ...(effectiveLimit && {
      limit: effectiveLimit,
      offset: calculatePageOffset(queryParams.page || 1, effectiveLimit)
    })
  };

  // this param is called searchTerm in the apis, search is replaced by that param earlier
  // page param is splitted in offset and limit
  const omitOldPropsRenamedToMatchApi = ['search', 'page'];
  if (params.sort === '') {
    omitOldPropsRenamedToMatchApi.push('sort');
  }
  return omit(params, omitOldPropsRenamedToMatchApi);
};

/**
 * Update query param dictated in function parameters (key and value)
 * and remove it if the value is empty:
 * - Array - length of 0 means we don't want to send any filter in the query
 * - string - Empty string means we don't want to send any search term in the query
 * @param previousParams - Previous state of query params
 * @param key - identifier of the updated param
 * @param value - value of the updated param
 */
export const addOrDeleteQueryParam = (previousParams: Record<string, any>, key: string, value: any) => {
  if (Array.isArray(value) && value.length === 0 || !value) {
    const {
      [key]: _,
      ...rest
    } = previousParams;
    return rest;
  }
  return {
    ...previousParams,
    [key]: value
  };
};